import React, { useState, useEffect, useMemo } from 'react';
import { apiService, TradingLogEntry } from '../services/api';

const PER_PAGE_OPTIONS = [10, 20, 50, 100];
const LIMIT_OPTIONS = [50, 100, 200, 500];

const TradingLogs: React.FC = () => {
  const [logs, setLogs] = useState<TradingLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(200);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const totalItems = logs.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const startIndex = (page - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, totalItems);
  const pageLogs = useMemo(
    () => logs.slice(startIndex, endIndex),
    [logs, startIndex, endIndex]
  );

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getLogs(limit);
      setLogs(Array.isArray(data.logs) ? data.logs : []);
      setPage(1);
    } catch (err) {
      console.error('Failed to fetch trading logs:', err);
      setError('Failed to load trading logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 60000);
    return () => clearInterval(interval);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const goToPage = (p: number) => setPage(Math.max(1, Math.min(p, totalPages)));

  const paginationItems = useMemo(() => {
    const items: (number | 'ellipsis')[] = [];
    if (totalPages <= 0) return items;
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
      return items;
    }
    const showLeft = page > 3;
    const showRight = page < totalPages - 2;
    items.push(1);
    if (showLeft) items.push('ellipsis');
    const low = showLeft ? Math.max(2, page - 1) : 2;
    const high = showRight ? Math.min(totalPages - 1, page + 1) : totalPages - 1;
    for (let i = low; i <= high; i++) {
      if (i !== 1 && i !== totalPages) items.push(i);
    }
    if (showRight) items.push('ellipsis');
    if (totalPages > 1) items.push(totalPages);
    return items;
  }, [page, totalPages]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl border border-slate-700/50 shadow-xl">
      <div className="p-6 border-b border-slate-700/50">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center text-white text-lg shadow-lg">
              📋
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Trading Logs</h2>
              <p className="text-xs text-slate-400 mt-0.5">Market open & close events</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={limit}
              onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value, 10) || 50))}
              className="px-4 py-2 bg-slate-700/50 border border-slate-600 text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  Last {n}
                </option>
              ))}
            </select>
            {logs.length > 0 && (
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-2 bg-slate-700/50 border border-slate-600 text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} per page
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={fetchLogs}
              disabled={loading}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>
      <div className="p-6">
        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        {loading && logs.length === 0 ? (
          <div className="text-center py-8 text-slate-400">Loading logs…</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8 text-slate-400">No trading logs yet.</div>
        ) : (
          <>
            {totalItems > 0 && (
              <p className="text-sm text-slate-400 mb-4">
                Showing {startIndex + 1}–{endIndex} of {totalItems} logs
              </p>
            )}
            <div className="space-y-4">
            {pageLogs.map((entry) => (
              <div
                key={entry.id}
                className={`rounded-lg border p-4 ${
                  entry.skipped
                    ? 'bg-amber-500/5 border-amber-500/30'
                    : entry.type === 'MARKET_OPEN'
                    ? 'bg-emerald-500/5 border-emerald-500/30'
                    : 'bg-blue-500/5 border-blue-500/30'
                }`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-semibold ${
                      entry.type === 'MARKET_OPEN'
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}
                  >
                    {entry.type.replace('_', ' ')}
                  </span>
                  <span className="text-slate-400 text-sm font-mono">{formatDate(entry.timestamp)}</span>
                  {entry.skipped && (
                    <span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400">Skipped</span>
                  )}
                </div>
                {(entry.reason || entry.skipReason || entry.decision) && (
                  <p className="text-sm text-slate-300 mt-1">
                    {entry.skipReason ?? entry.reason ?? entry.decision ?? ''}
                  </p>
                )}
                {entry.executions && entry.executions.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm text-slate-400">
                    {entry.executions.map((ex, i) => (
                      <li key={i}>
                        {ex.action} {ex.quantity} {ex.symbol}
                        {ex.status && ` · ${ex.status}`}
                        {ex.error && ` · ${ex.error}`}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
          {logs.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <span className="text-sm text-slate-400">
                Page {page} of {totalPages}
              </span>
              <nav
                className="inline-flex items-center rounded-lg overflow-hidden border border-slate-600 bg-slate-800/80"
                aria-label="Trading logs pagination"
              >
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700/50 border-r border-slate-600 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700/50 transition-colors"
                >
                  Prev
                </button>
                <div className="flex items-center">
                  {paginationItems.map((item, idx) =>
                    item === 'ellipsis' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-3 py-2 text-slate-500 text-sm"
                        aria-hidden
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        type="button"
                        onClick={() => goToPage(item)}
                        className={`min-w-[2.5rem] px-3 py-2 text-sm font-medium transition-colors ${
                          item === page
                            ? 'bg-blue-600 text-white'
                            : 'text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {item}
                      </button>
                    )
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                  className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700/50 border-l border-slate-600 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-700/50 transition-colors"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default TradingLogs;
